import React from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { costRfqStatusVariant } from '@/lib/status-variants'
import { cn } from '@/lib/utils'
import { formatIsoDate } from '../../schedule/lib/format'
import { RFQ_STATUS_KEY } from '../lib/labels'
import { formatMoney } from '../lib/money'
import { allQuoted, isRfqOverdue, rfqTypeText } from '../lib/rfq'
import type { CostRfq } from '../types'

interface Props { rows: CostRfq[]; currency: string; locale: string; today: string; onOpen: (id: string) => void }

/** Part D spec §8: number, title, type, budget item, due date, quotes asked/quoted, status and the winner. */
const RfqsTable: React.FC<Props> = ({ rows, currency, locale, today, onOpen }) => {
  const { t } = useTranslation()
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-16">{t('CostControl_rfq_col_number')}</TableHead>
            <TableHead>{t('CostControl_rfq_field_title')}</TableHead>
            <TableHead>{t('CostControl_rfq_field_type')}</TableHead>
            <TableHead>{t('CostControl_rfq_field_node')}</TableHead>
            <TableHead className="w-32">{t('CostControl_rfq_field_dueDate')}</TableHead>
            <TableHead className="w-24 text-right">{t('CostControl_rfq_col_quotes')}</TableHead>
            <TableHead className="w-48">{t('CostControl_contract_field_status')}</TableHead>
            <TableHead className="w-48 text-right">{t('CostControl_rfq_col_winner')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id} className="cursor-pointer" onClick={() => onOpen(r.id)}>
              <TableCell className="font-mono text-xs">{r.number}</TableCell>
              <TableCell>
                <button type="button" className="text-left font-medium text-primary hover:underline" onClick={(e) => { e.stopPropagation(); onOpen(r.id) }}>{r.title}</button>
              </TableCell>
              <TableCell className="text-sm">{rfqTypeText(r, t)}</TableCell>
              <TableCell className="text-sm">
                {r.nodeName ? (
                  <>
                    {r.nodePath && <div className="text-xs text-muted-foreground">{r.nodePath}</div>}
                    <div>{r.nodeName}</div>
                  </>
                ) : '—'}
              </TableCell>
              <TableCell className={cn('text-sm', isRfqOverdue(r, today) && 'font-medium text-destructive')}>{r.dueDate ? formatIsoDate(t, r.dueDate) : '—'}</TableCell>
              <TableCell className="text-right tabular-nums">{`${r.quotedCount}/${r.askedCount}`}</TableCell>
              <TableCell>
                <div className="flex flex-wrap items-center gap-1">
                  <Badge variant={costRfqStatusVariant(r.status)}>{t(RFQ_STATUS_KEY[r.status])}</Badge>
                  {allQuoted(r) && <Badge variant="warning">{t('CostControl_rfq_all_quoted')}</Badge>}
                </div>
              </TableCell>
              <TableCell className="text-right text-sm">
                {r.winnerName ? (
                  <>
                    <div>{r.winnerName}</div>
                    {r.winnerAmount && <div className="tabular-nums text-muted-foreground">{formatMoney(r.winnerAmount, { locale, currency })}</div>}
                  </>
                ) : '—'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
export default RfqsTable
